import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import {
  getPreTransformedStructureWithRooms,
  getPreTransformedRoom,
} from '../services/preTransformedStructureService';
import {
  markStructureObject,
  isStructureObject,
} from '../state/runtimeMeshState';
import '../styles/PreTransformedViewer.css';
import { releaseRenderer } from '../utils/releaseRenderer';

const PreTransformedViewer = ({ structureId, roomId, onClose }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const frameId = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!structureId && !roomId) return;

    // Initialize Three.js scene
    const initScene = () => {
      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      // Scene
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf0f0f0);
      sceneRef.current = scene;

      // Camera
      const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      camera.position.set(10, 10, 10);
      cameraRef.current = camera;

      // Renderer
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      mountRef.current.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // Controls
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controlsRef.current = controls;

      // Lights
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.4);
      directionalLight.position.set(10, 20, 10);
      directionalLight.castShadow = true;
      directionalLight.shadow.camera.near = 0.1;
      directionalLight.shadow.camera.far = 50;
      directionalLight.shadow.camera.left = -20;
      directionalLight.shadow.camera.right = 20;
      directionalLight.shadow.camera.top = 20;
      directionalLight.shadow.camera.bottom = -20;
      scene.add(directionalLight);

      // Enhanced axis visualization
      createAxisVisualization(scene);
    };

    // Create enhanced axis visualization
    const createAxisVisualization = (scene) => {
      // Remove default grid
      // const gridHelper = new THREE.GridHelper(20, 20);
      // scene.add(gridHelper);

      // Create custom grid with 4' (1.2192m) spacing
      const gridSize = 40; // 40 feet total
      const gridDivisions = 10; // Every 4 feet
      const gridSpacing = 1.2192; // 4 feet in meters

      // Create grid lines
      const gridMaterial = new THREE.LineBasicMaterial({ color: 0x444444 });
      const gridGeometry = new THREE.BufferGeometry();
      const gridVertices = [];

      for (let i = -5; i <= 5; i++) {
        const pos = i * gridSpacing;
        // Lines parallel to X axis
        gridVertices.push(-5 * gridSpacing, 0, pos);
        gridVertices.push(5 * gridSpacing, 0, pos);
        // Lines parallel to Z axis
        gridVertices.push(pos, 0, -5 * gridSpacing);
        gridVertices.push(pos, 0, 5 * gridSpacing);
      }

      gridGeometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(gridVertices, 3)
      );
      const gridLines = new THREE.LineSegments(gridGeometry, gridMaterial);
      // scene.add(gridLines);

      // Create axis lines with different colors
      const axisLength = 6.096; // 20 feet in meters

      // X axis (red)
      const xAxisGeometry = new THREE.BufferGeometry();
      xAxisGeometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(
          [-axisLength, 0, 0, axisLength, 0, 0],
          3
        )
      );
      const xAxis = new THREE.Line(
        xAxisGeometry,
        new THREE.LineBasicMaterial({ color: 0xff0000, linewidth: 2 })
      );
      scene.add(xAxis);

      // Y axis (green)
      const yAxisGeometry = new THREE.BufferGeometry();
      yAxisGeometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(
          [0, -axisLength / 4, 0, 0, axisLength, 0],
          3
        )
      );
      const yAxis = new THREE.Line(
        yAxisGeometry,
        new THREE.LineBasicMaterial({ color: 0x00ff00, linewidth: 2 })
      );
      scene.add(yAxis);

      // Z axis (blue)
      const zAxisGeometry = new THREE.BufferGeometry();
      zAxisGeometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(
          [0, 0, -axisLength, 0, 0, axisLength],
          3
        )
      );
      const zAxis = new THREE.Line(
        zAxisGeometry,
        new THREE.LineBasicMaterial({ color: 0x0000ff, linewidth: 2 })
      );
      scene.add(zAxis);

      // Add origin marker
      const originGeometry = new THREE.SphereGeometry(0.1, 16, 16);
      const originMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const originMarker = new THREE.Mesh(originGeometry, originMaterial);
      scene.add(originMarker);

      // Add text labels using sprites
      const createTextSprite = (text, color) => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 256;
        canvas.height = 64;

        context.fillStyle = '#000000';
        context.fillRect(0, 0, canvas.width, canvas.height);

        context.font = 'Bold 48px Arial';
        context.fillStyle = color;
        context.textAlign = 'center';
        context.fillText(text, 128, 48);

        const texture = new THREE.CanvasTexture(canvas);
        const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(0.5, 0.125, 1);

        return sprite;
      };

      // Add axis labels
      const xLabel = createTextSprite('X', '#ff0000');
      xLabel.position.set(axisLength + 0.5, 0, 0);
      scene.add(xLabel);

      const yLabel = createTextSprite('Y', '#00ff00');
      yLabel.position.set(0, axisLength + 0.5, 0);
      scene.add(yLabel);

      const zLabel = createTextSprite('Z', '#0000ff');
      zLabel.position.set(0, 0, axisLength + 0.5);
      scene.add(zLabel);

      // Add origin label
      const originLabel = createTextSprite('0,0,0', '#ffffff');
      originLabel.position.set(0.5, 0.5, 0.5);
      scene.add(originLabel);

      // Add distance markers every 4 feet
      for (let i = 1; i <= 5; i++) {
        const distance = i * gridSpacing;
        const feetLabel = `${i * 4}'`;

        // X axis markers
        const xMarkerPos = createTextSprite(feetLabel, '#ff0000');
        xMarkerPos.position.set(distance, 0.2, 0);
        scene.add(xMarkerPos);

        const xMarkerNeg = createTextSprite(`-${feetLabel}`, '#ff0000');
        xMarkerNeg.position.set(-distance, 0.2, 0);
        scene.add(xMarkerNeg);

        // Z axis markers
        const zMarkerPos = createTextSprite(feetLabel, '#0000ff');
        zMarkerPos.position.set(0, 0.2, distance);
        scene.add(zMarkerPos);

        const zMarkerNeg = createTextSprite(`-${feetLabel}`, '#0000ff');
        zMarkerNeg.position.set(0, 0.2, -distance);
        scene.add(zMarkerNeg);

        // Y axis markers (only positive)
        if (i <= 4) {
          const yMarker = createTextSprite(feetLabel, '#00ff00');
          yMarker.position.set(0.3, distance, 0);
          scene.add(yMarker);
        }
      }
    };

    // Load and render structure or room data
    const loadStructure = async () => {
      try {
        setLoading(true);

        let data;
        if (roomId) {
          // Load single room
          const room = await getPreTransformedRoom(roomId);
          if (!room) {
            setError('Room not found');
            return;
          }
          data = { rooms: [room] };
        } else if (structureId) {
          // Load structure with rooms
          data = await getPreTransformedStructureWithRooms(structureId);
          console.log('Loaded structure data:', data);
        } else {
          setError('No structure or room ID provided');
          return;
        }

        // Check if we have room data
        let roomsToRender = [];

        if (data.rooms && Array.isArray(data.rooms) && data.rooms.length > 0) {
          roomsToRender = data.rooms;
          console.log(`Found ${roomsToRender.length} rooms to render`);
        } else if (
          data.structure &&
          data.structure.rooms &&
          Array.isArray(data.structure.rooms)
        ) {
          // Some structures might have rooms embedded
          roomsToRender = data.structure.rooms;
          console.log(`Found ${roomsToRender.length} embedded rooms to render`);
        } else {
          console.error('No room data found. Structure data:', data.structure);
          console.error('Rooms data:', data.rooms);
          setError('No room data found. Check console for details.');
          return;
        }

        // Clear existing structure objects
        const objectsToRemove = [];
        sceneRef.current.traverse((child) => {
          if (isStructureObject(child.uuid)) {
            objectsToRemove.push(child);
          }
        });
        objectsToRemove.forEach((obj) => sceneRef.current.remove(obj));

        // Process rooms
        roomsToRender.forEach((room, roomIndex) => {
          console.log(`Processing room ${roomIndex}:`, room);

          // Render floors
          if (room.floors && Array.isArray(room.floors)) {
            room.floors.forEach((floor, floorIndex) => {
              if (
                !floor.dimensions ||
                floor.dimensions.x == null ||
                floor.dimensions.z == null
              ) {
                console.error(
                  `Floor ${floorIndex} in room ${roomIndex} missing dimensions:`,
                  floor
                );
                return;
              }
              if (
                !floor.center ||
                floor.center.x == null ||
                floor.center.y == null ||
                floor.center.z == null
              ) {
                console.error(
                  `Floor ${floorIndex} in room ${roomIndex} missing center:`,
                  floor
                );
                return;
              }

              // Floor dimensions are in the floor's local coordinate system
              // In floor's local space: X = width, Y = depth, Z = thickness
              const geometry = new THREE.BoxGeometry(
                floor.dimensions.x, // Width in local X
                floor.dimensions.y, // Depth in local Y
                floor.dimensions.z
              );

              const material = new THREE.MeshStandardMaterial({
                color: 0x808080,
                opacity: 0.8,
                transparent: true,
              });
              const mesh = new THREE.Mesh(geometry, material);

              // Center is already in world coordinates
              mesh.position.set(floor.center.x, floor.center.y, floor.center.z);

              // Apply rotation if provided
              if (floor.rotation) {
                console.log(`Floor ${floorIndex} rotation:`, {
                  x: floor.rotation.x,
                  y: floor.rotation.y,
                  z: floor.rotation.z,
                  xDeg: ((floor.rotation.x * 180) / Math.PI).toFixed(2),
                  yDeg: ((floor.rotation.y * 180) / Math.PI).toFixed(2),
                  zDeg: ((floor.rotation.z * 180) / Math.PI).toFixed(2),
                });
                mesh.rotation.x = floor.rotation.x || 0;
                mesh.rotation.y = floor.rotation.y || 0;
                mesh.rotation.z = floor.rotation.z || 0;
              } else {
                console.log(`Floor ${floorIndex} has no rotation`);
              }

              markStructureObject(mesh.uuid);
              mesh.castShadow = true;
              mesh.receiveShadow = true;
              sceneRef.current.add(mesh);

              // Add a highly visible plane to visualize the floor's orientation
              const planeGeometry = new THREE.PlaneGeometry(
                floor.dimensions.x * 2.0, // Make it much larger
                floor.dimensions.z * 2.0
              );

              // Create a bright, more visible material
              const planeMaterial = new THREE.MeshBasicMaterial({
                color: 0x00ff00, // Bright green
                transparent: true,
                opacity: 0.6, // More opaque
                side: THREE.DoubleSide,
                emissive: 0x00ff00,
                emissiveIntensity: 0.3,
              });
              const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);

              // Position at floor center
              planeMesh.position.copy(mesh.position);

              // Apply same rotation as floor
              planeMesh.rotation.copy(mesh.rotation);

              // Add thick edge lines to make the plane more visible
              const edgesGeometry = new THREE.EdgesGeometry(planeGeometry);
              const edgesMaterial = new THREE.LineBasicMaterial({
                color: 0x00ff00,
                linewidth: 3, // Note: linewidth may not work in all renderers
              });
              const edgeLines = new THREE.LineSegments(
                edgesGeometry,
                edgesMaterial
              );
              edgeLines.position.copy(mesh.position);
              edgeLines.rotation.copy(mesh.rotation);

              // Add wireframe for better visibility
              const wireframeGeometry = new THREE.WireframeGeometry(
                planeGeometry
              );
              const wireframeMaterial = new THREE.LineBasicMaterial({
                color: 0x00ff00,
                opacity: 0.8,
                transparent: true,
              });
              const wireframe = new THREE.LineSegments(
                wireframeGeometry,
                wireframeMaterial
              );
              wireframe.position.copy(mesh.position);
              wireframe.rotation.copy(mesh.rotation);

              markStructureObject(planeMesh.uuid);
              markStructureObject(edgeLines.uuid);
              markStructureObject(wireframe.uuid);
              sceneRef.current.add(planeMesh);
              sceneRef.current.add(edgeLines);
              sceneRef.current.add(wireframe);

              // Add larger coordinate axes at floor center to show orientation
              const axesHelper = new THREE.AxesHelper(2); // Larger axes
              axesHelper.position.copy(mesh.position);
              axesHelper.rotation.copy(mesh.rotation);
              markStructureObject(axesHelper.uuid);
              sceneRef.current.add(axesHelper);

              // Add a grid on the plane for better visualization
              const gridHelper = new THREE.GridHelper(
                Math.max(floor.dimensions.x, floor.dimensions.y) * 2,
                10,
                0x00ff00,
                0x00aa00
              );
              gridHelper.position.copy(mesh.position);
              gridHelper.rotation.copy(mesh.rotation);
              // Grid is created on XZ plane, rotate to match plane orientation
              gridHelper.rotateX(Math.PI / 2);
              markStructureObject(gridHelper.uuid);
              sceneRef.current.add(gridHelper);

              // Visualize polygon corners if they exist
              if (floor.polygonCorners && Array.isArray(floor.polygonCorners)) {
                console.log(
                  `Floor ${floorIndex} polygon corners count: ${floor.polygonCorners.length}`
                );
                console.log(
                  `Floor ${floorIndex} polygon corners:`,
                  floor.polygonCorners
                );
                console.log(
                  `Floor ${floorIndex} dimensions:`,
                  floor.dimensions
                );
                console.log(`Floor ${floorIndex} center:`, floor.center);

                const rotatedPoints = [];

                // Check if we actually have polygon corners data
                if (floor.polygonCorners.length === 0) {
                  console.warn(
                    `Floor ${floorIndex} has empty polygonCorners array`
                  );
                }

                // The polygon corners from Firestore are already transformed to world coordinates
                // by the Firebase function (transformCornersTo3D), so we display them as-is

                // Visualize the actual transformed corners from Firestore (red)
                floor.polygonCorners.forEach((corner, cornerIndex) => {
                  let x, y, z;
                  if (typeof corner === 'object' && corner.x != null) {
                    x = corner.x;
                    y = corner.y || floor.center.y || 0;
                    z = corner.z;
                  } else {
                    console.warn(
                      `Floor ${floorIndex} corner ${cornerIndex} has unexpected format:`,
                      corner
                    );
                    return;
                  }

                  // Red sphere for actual (rotated) corner position
                  const sphereGeometry = new THREE.SphereGeometry(0.05, 8, 8);
                  const sphereMaterial = new THREE.MeshBasicMaterial({
                    color: 0xff0000,
                  });
                  const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
                  sphere.position.set(x, y, z);
                  markStructureObject(sphere.uuid);
                  sceneRef.current.add(sphere);

                  rotatedPoints.push(new THREE.Vector3(x, y, z));
                });

                // Draw red line for actual polygon from Firestore
                if (rotatedPoints.length > 0) {
                  rotatedPoints.push(rotatedPoints[0]);
                  const lineGeometry = new THREE.BufferGeometry().setFromPoints(
                    rotatedPoints
                  );
                  const lineMaterial = new THREE.LineBasicMaterial({
                    color: 0xff0000,
                    linewidth: 2,
                  });
                  const line = new THREE.Line(lineGeometry, lineMaterial);
                  markStructureObject(line.uuid);
                  sceneRef.current.add(line);
                }
              }
            });
          }

          // Render walls
          if (room.walls && Array.isArray(room.walls)) {
            room.walls.forEach((wall, wallIndex) => {
              if (
                !wall.dimensions ||
                wall.dimensions.x == null ||
                wall.dimensions.y == null ||
                wall.dimensions.z == null
              ) {
                console.error(
                  `Wall ${wallIndex} in room ${roomIndex} missing dimensions:`,
                  wall
                );
                return;
              }
              if (
                !wall.center ||
                wall.center.x == null ||
                wall.center.y == null ||
                wall.center.z == null
              ) {
                console.error(
                  `Wall ${wallIndex} in room ${roomIndex} missing center:`,
                  wall
                );
                return;
              }

              console.log(`Wall ${wallIndex} in room ${roomIndex} details:`, {
                center: wall.center,
                dimensions: wall.dimensions,
                id: wall.id,
              });

              const geometry = new THREE.BoxGeometry(
                wall.dimensions.x,
                wall.dimensions.y,
                wall.dimensions.z
              );
              const material = new THREE.MeshStandardMaterial({
                color: 0xcccccc,
              });
              const mesh = new THREE.Mesh(geometry, material);

              // Center is already in world coordinates
              mesh.position.set(wall.center.x, wall.center.y, wall.center.z);

              // Check for flipped normals (180° in both X and Z)
              const hasFlipRotation =
                wall.rotation &&
                Math.abs(wall.rotation.x - Math.PI) < 0.01 &&
                Math.abs(wall.rotation.z - Math.PI) < 0.01;

              // Apply rotation if provided
              if (wall.rotation) {
                console.log(
                  `Wall ${wallIndex} in room ${roomIndex} rotation:`,
                  {
                    x: wall.rotation.x,
                    y: wall.rotation.y,
                    z: wall.rotation.z,
                    xDeg: ((wall.rotation.x * 180) / Math.PI).toFixed(2),
                    yDeg: ((wall.rotation.y * 180) / Math.PI).toFixed(2),
                    zDeg: ((wall.rotation.z * 180) / Math.PI).toFixed(2),
                    hasXZFlip: hasFlipRotation,
                  }
                );

                if (hasFlipRotation) {
                  console.warn(
                    `Wall ${wallIndex} has 180° X and Z rotations - this may indicate the wall normal is flipped`
                  );
                }

                mesh.rotation.x = wall.rotation.x || 0;
                mesh.rotation.y = wall.rotation.y || 0;
                mesh.rotation.z = wall.rotation.z || 0;

                console.log(`Wall ${wallIndex} rotation applied:`, {
                  meshRotation: {
                    x: mesh.rotation.x,
                    y: mesh.rotation.y,
                    z: mesh.rotation.z,
                  },
                  meshRotationDeg: {
                    x: ((mesh.rotation.x * 180) / Math.PI).toFixed(2),
                    y: ((mesh.rotation.y * 180) / Math.PI).toFixed(2),
                    z: ((mesh.rotation.z * 180) / Math.PI).toFixed(2),
                  },
                });
              } else {
                console.log(
                  `Wall ${wallIndex} in room ${roomIndex} has no rotation`
                );
              }

              markStructureObject(mesh.uuid);
              mesh.castShadow = true;
              mesh.receiveShadow = true;
              sceneRef.current.add(mesh);

              // Visualize polygon corners if they exist
              if (wall.polygonCorners && Array.isArray(wall.polygonCorners)) {
                console.log(
                  `Wall ${wallIndex} polygon corners count: ${wall.polygonCorners.length}`
                );
                console.log(
                  `Wall ${wallIndex} polygon corners:`,
                  wall.polygonCorners
                );

                const rotatedPoints = [];

                // Check if we actually have polygon corners data
                if (wall.polygonCorners.length === 0) {
                  console.warn(
                    `Wall ${wallIndex} has empty polygonCorners array`
                  );
                }

                // The polygon corners from Firestore are already transformed to world coordinates
                // by the Firebase function (transformCornersTo3D), so we display them as-is

                // Visualize the actual transformed corners from Firestore (yellow for walls)
                wall.polygonCorners.forEach((corner, cornerIndex) => {
                  let x, y, z;
                  if (typeof corner === 'object' && corner.x != null) {
                    x = corner.x;
                    y = corner.y || wall.center.y || 0;
                    z = corner.z;
                  } else {
                    console.warn(
                      `Wall ${wallIndex} corner ${cornerIndex} has unexpected format:`,
                      corner
                    );
                    return;
                  }

                  // Yellow sphere for wall corner position
                  const sphereGeometry = new THREE.SphereGeometry(0.05, 8, 8);
                  const sphereMaterial = new THREE.MeshBasicMaterial({
                    color: 0xffff00, // Yellow for walls
                  });
                  const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
                  sphere.position.set(x, y, z);
                  markStructureObject(sphere.uuid);
                  sceneRef.current.add(sphere);

                  rotatedPoints.push(new THREE.Vector3(x, y, z));
                });

                // Draw yellow line for wall polygon from Firestore
                if (rotatedPoints.length > 0) {
                  rotatedPoints.push(rotatedPoints[0]);
                  const lineGeometry = new THREE.BufferGeometry().setFromPoints(
                    rotatedPoints
                  );
                  const lineMaterial = new THREE.LineBasicMaterial({
                    color: 0xffff00, // Yellow for walls
                    linewidth: 2,
                  });
                  const line = new THREE.Line(lineGeometry, lineMaterial);
                  markStructureObject(line.uuid);
                  sceneRef.current.add(line);
                }
              }

              // Add wall normal visualization
              const normalLength =
                Math.min(wall.dimensions.x, wall.dimensions.y) * 0.5;
              const normalGeometry = new THREE.ConeGeometry(
                0.05,
                normalLength,
                8
              );
              const normalMaterial = new THREE.MeshBasicMaterial({
                color: hasFlipRotation ? 0xff0000 : 0x00ff00, // Red for flipped normals, green for regular
              });
              const normalCone = new THREE.Mesh(normalGeometry, normalMaterial);

              // Position at wall center
              normalCone.position.copy(mesh.position);

              // Apply same rotation as wall
              normalCone.rotation.copy(mesh.rotation);

              // Rotate cone to point along Z axis (normal direction)
              normalCone.rotateX(Math.PI / 2);

              // Move cone so base is at wall surface
              normalCone.translateY(normalLength / 2 + wall.dimensions.z / 2);

              markStructureObject(normalCone.uuid);
              sceneRef.current.add(normalCone);

              // Add pre-rotation wall visualization
              if (
                wall.rotation &&
                (Math.abs(wall.rotation.x) > 0.01 ||
                  Math.abs(wall.rotation.y) > 0.01 ||
                  Math.abs(wall.rotation.z) > 0.01)
              ) {
                // Create translucent blue wall at original position (no rotation)
                const preRotatedGeometry = new THREE.BoxGeometry(
                  wall.dimensions.x,
                  wall.dimensions.y,
                  wall.dimensions.z
                );
                const preRotatedMaterial = new THREE.MeshStandardMaterial({
                  color: 0x0080ff,
                  transparent: true,
                  opacity: 0.3,
                  side: THREE.DoubleSide,
                });
                const preRotatedMesh = new THREE.Mesh(
                  preRotatedGeometry,
                  preRotatedMaterial
                );

                // Position at same center but without rotation
                preRotatedMesh.position.set(
                  wall.center.x,
                  wall.center.y,
                  wall.center.z
                );
                // No rotation applied - this shows the pre-rotation position

                markStructureObject(preRotatedMesh.uuid);
                sceneRef.current.add(preRotatedMesh);

                // Create arrow from pre-rotated to post-rotated wall
                // Get corners of pre-rotated wall
                const preRotatedCorners = [
                  new THREE.Vector3(
                    -wall.dimensions.x / 2,
                    wall.dimensions.y / 2,
                    0
                  ),
                  new THREE.Vector3(
                    wall.dimensions.x / 2,
                    wall.dimensions.y / 2,
                    0
                  ),
                  new THREE.Vector3(
                    wall.dimensions.x / 2,
                    -wall.dimensions.y / 2,
                    0
                  ),
                  new THREE.Vector3(
                    -wall.dimensions.x / 2,
                    -wall.dimensions.y / 2,
                    0
                  ),
                ];

                // Transform corners to world space (just translation for pre-rotated)
                const preRotatedWorldCorners = preRotatedCorners.map((corner) =>
                  corner
                    .clone()
                    .add(
                      new THREE.Vector3(
                        wall.center.x,
                        wall.center.y,
                        wall.center.z
                      )
                    )
                );

                // Get corners of post-rotated wall
                const postRotatedCorners = preRotatedCorners.map((corner) => {
                  // Apply rotation
                  const rotated = corner.clone();
                  rotated.applyEuler(
                    new THREE.Euler(
                      wall.rotation.x || 0,
                      wall.rotation.y || 0,
                      wall.rotation.z || 0,
                      'XYZ'
                    )
                  );
                  // Then translate
                  return rotated.add(
                    new THREE.Vector3(
                      wall.center.x,
                      wall.center.y,
                      wall.center.z
                    )
                  );
                });

                // Draw rotation arcs from each pre-rotated corner to corresponding post-rotated corner
                preRotatedWorldCorners.forEach((preCorner, idx) => {
                  const postCorner = postRotatedCorners[idx];
                  const distance = preCorner.distanceTo(postCorner);

                  if (distance > 0.05) {
                    // Only show arc if there's significant movement
                    // Calculate the rotation arc path
                    // The corner rotates around the wall's center
                    const center = new THREE.Vector3(
                      wall.center.x,
                      wall.center.y,
                      wall.center.z
                    );

                    // Get local positions relative to center
                    const preLocal = preCorner.clone().sub(center);
                    const postLocal = postCorner.clone().sub(center);

                    // Calculate radius and angles
                    const actualRadius = preLocal.length();

                    // Project onto XZ plane for Y rotation visualization
                    const preAngle = Math.atan2(preLocal.z, preLocal.x);
                    const postAngle = Math.atan2(postLocal.z, postLocal.x);

                    // Calculate the arc
                    let angleStart = preAngle;
                    let angleEnd = postAngle;
                    let angleDiff = angleEnd - angleStart;

                    // Normalize angle difference to [-PI, PI]
                    while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
                    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

                    // Create arc points
                    const arcPoints = [];
                    const steps = 30;

                    // For a perfect arc that passes through both corners, we need to account for Y movement
                    // Calculate the radius in the XZ plane
                    const radiusXZ = Math.sqrt(
                      preLocal.x * preLocal.x + preLocal.z * preLocal.z
                    );

                    // Draw the arc
                    for (let i = 0; i <= steps; i++) {
                      const t = i / steps;
                      const angle = angleStart + angleDiff * t;

                      // Arc in XZ plane at the correct radius
                      const x = center.x + radiusXZ * Math.cos(angle);
                      const z = center.z + radiusXZ * Math.sin(angle);

                      // Interpolate Y linearly
                      const y = preCorner.y + (postCorner.y - preCorner.y) * t;

                      arcPoints.push(new THREE.Vector3(x, y, z));
                    }

                    // Create arc line
                    const arcGeometry =
                      new THREE.BufferGeometry().setFromPoints(arcPoints);
                    const arcMaterial = new THREE.LineBasicMaterial({
                      color: 0xff8800, // Orange arcs
                      linewidth: 2,
                      opacity: 0.8,
                      transparent: true,
                    });
                    const arcLine = new THREE.Line(arcGeometry, arcMaterial);
                    markStructureObject(arcLine.uuid);
                    sceneRef.current.add(arcLine);

                    // Create arrowhead pointing along the arc's tangent
                    // Get tangent direction at the end of the arc
                    const tangentAngle =
                      angleEnd + (angleDiff > 0 ? Math.PI / 2 : -Math.PI / 2);
                    const tangent = new THREE.Vector3(
                      Math.cos(tangentAngle),
                      0,
                      Math.sin(tangentAngle)
                    );
                    const direction = tangent;
                    const arrowheadLength = 0.1;
                    const arrowheadAngle = 0.5;

                    // Calculate perpendicular vectors for arrowhead
                    const perpVector = new THREE.Vector3();
                    if (Math.abs(direction.y) < 0.9) {
                      perpVector
                        .crossVectors(direction, new THREE.Vector3(0, 1, 0))
                        .normalize();
                    } else {
                      perpVector
                        .crossVectors(direction, new THREE.Vector3(1, 0, 0))
                        .normalize();
                    }

                    const perpVector2 = new THREE.Vector3()
                      .crossVectors(direction, perpVector)
                      .normalize();

                    // Create 3D arrowhead
                    const arrowBase = postCorner
                      .clone()
                      .sub(direction.clone().multiplyScalar(arrowheadLength));
                    const wing1 = arrowBase
                      .clone()
                      .add(
                        perpVector
                          .clone()
                          .multiplyScalar(
                            arrowheadLength * Math.tan(arrowheadAngle)
                          )
                      );
                    const wing2 = arrowBase
                      .clone()
                      .sub(
                        perpVector
                          .clone()
                          .multiplyScalar(
                            arrowheadLength * Math.tan(arrowheadAngle)
                          )
                      );
                    const wing3 = arrowBase
                      .clone()
                      .add(
                        perpVector2
                          .clone()
                          .multiplyScalar(
                            arrowheadLength * Math.tan(arrowheadAngle)
                          )
                      );
                    const wing4 = arrowBase
                      .clone()
                      .sub(
                        perpVector2
                          .clone()
                          .multiplyScalar(
                            arrowheadLength * Math.tan(arrowheadAngle)
                          )
                      );

                    // Create arrowhead lines
                    const arrowheadGeometry =
                      new THREE.BufferGeometry().setFromPoints([
                        postCorner,
                        wing1,
                        postCorner,
                        wing2,
                        postCorner,
                        wing3,
                        postCorner,
                        wing4,
                      ]);
                    const arrowheadLines = new THREE.LineSegments(
                      arrowheadGeometry,
                      arcMaterial
                    );
                    markStructureObject(arrowheadLines.uuid);
                    sceneRef.current.add(arrowheadLines);
                  }
                });
              }

              // Add wall number label
              const canvas = document.createElement('canvas');
              const context = canvas.getContext('2d');
              canvas.width = 256;
              canvas.height = 128;

              context.fillStyle = '#000000';
              context.fillRect(0, 0, canvas.width, canvas.height);

              context.font = 'Bold 72px Arial';
              context.fillStyle = '#ffffff';
              context.textAlign = 'center';
              context.fillText(`Wall ${wallIndex}`, 128, 80);

              const texture = new THREE.CanvasTexture(canvas);
              const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
              const sprite = new THREE.Sprite(spriteMaterial);
              sprite.scale.set(0.5, 0.25, 1);
              sprite.position.copy(mesh.position);
              markStructureObject(sprite.uuid);
              sceneRef.current.add(sprite);

              // Add rotation values as compact vertical vector
              if (wall.rotation) {
                const rotCanvas = document.createElement('canvas');
                const rotContext = rotCanvas.getContext('2d');
                rotCanvas.width = 128;
                rotCanvas.height = 192;

                // Clear background
                rotContext.fillStyle = 'rgba(0, 0, 0, 0.8)';
                rotContext.fillRect(0, 0, rotCanvas.width, rotCanvas.height);

                // Draw rotation values
                rotContext.font = '20px monospace';
                rotContext.fillStyle = '#00ff00';
                rotContext.textAlign = 'center';

                const rx = wall.rotation.x || 0;
                const ry = wall.rotation.y || 0;
                const rz = wall.rotation.z || 0;

                // Draw as vertical vector
                rotContext.fillText(rx.toFixed(3), 64, 50);
                rotContext.fillText(ry.toFixed(3), 64, 100);
                rotContext.fillText(rz.toFixed(3), 64, 150);

                // Add brackets
                rotContext.strokeStyle = '#00ff00';
                rotContext.lineWidth = 2;
                rotContext.beginPath();
                rotContext.moveTo(20, 30);
                rotContext.lineTo(10, 30);
                rotContext.lineTo(10, 160);
                rotContext.lineTo(20, 160);
                rotContext.moveTo(108, 30);
                rotContext.lineTo(118, 30);
                rotContext.lineTo(118, 160);
                rotContext.lineTo(108, 160);
                rotContext.stroke();

                const rotTexture = new THREE.CanvasTexture(rotCanvas);
                const rotSpriteMaterial = new THREE.SpriteMaterial({
                  map: rotTexture,
                  transparent: true,
                });
                const rotSprite = new THREE.Sprite(rotSpriteMaterial);
                rotSprite.scale.set(0.3, 0.45, 1);
                rotSprite.position.set(
                  mesh.position.x + wall.dimensions.x * 0.7,
                  mesh.position.y,
                  mesh.position.z
                );
                markStructureObject(rotSprite.uuid);
                sceneRef.current.add(rotSprite);
              }

              // Add rotation indicator arc
              if (
                wall.rotation &&
                (wall.rotation.x !== 0 ||
                  wall.rotation.y !== 0 ||
                  wall.rotation.z !== 0)
              ) {
                // Create arc to show Y rotation (most common for walls)
                const yRotationDeg = (wall.rotation.y * 180) / Math.PI;
                const absRotation = Math.abs(wall.rotation.y);

                if (absRotation > 0.01) {
                  // Only show if rotation is significant
                  // Create arc geometry
                  const arcRadius = 0.5;
                  const arcSegments = Math.max(8, Math.floor(absRotation * 10));
                  const arcPoints = [];

                  // Generate points for the arc
                  for (let i = 0; i <= arcSegments; i++) {
                    const angle = (i / arcSegments) * wall.rotation.y;
                    arcPoints.push(
                      new THREE.Vector3(
                        Math.sin(angle) * arcRadius,
                        0,
                        Math.cos(angle) * arcRadius
                      )
                    );
                  }

                  // Create the arc line
                  const arcGeometry = new THREE.BufferGeometry().setFromPoints(
                    arcPoints
                  );
                  const arcMaterial = new THREE.LineBasicMaterial({
                    color: 0x0080ff, // Blue for Y rotation
                    linewidth: 2,
                  });
                  const arcLine = new THREE.Line(arcGeometry, arcMaterial);
                  arcLine.position.copy(mesh.position);
                  markStructureObject(arcLine.uuid);
                  sceneRef.current.add(arcLine);

                  // Add arrowhead at the end of the arc
                  const endAngle = wall.rotation.y;
                  const arrowTip = new THREE.Vector3(
                    Math.sin(endAngle) * arcRadius,
                    0,
                    Math.cos(endAngle) * arcRadius
                  );

                  // Calculate arrow direction (tangent to the arc)
                  const arrowDir = new THREE.Vector3(
                    Math.cos(endAngle) * Math.sign(wall.rotation.y),
                    0,
                    -Math.sin(endAngle) * Math.sign(wall.rotation.y)
                  ).normalize();

                  // Create arrowhead
                  const arrowLength = 0.1;
                  const arrowAngle = 0.5; // radians

                  // Calculate arrow wing points
                  const wing1 = arrowTip.clone().add(
                    arrowDir
                      .clone()
                      .multiplyScalar(-arrowLength)
                      .applyAxisAngle(new THREE.Vector3(0, 1, 0), arrowAngle)
                  );
                  const wing2 = arrowTip.clone().add(
                    arrowDir
                      .clone()
                      .multiplyScalar(-arrowLength)
                      .applyAxisAngle(new THREE.Vector3(0, 1, 0), -arrowAngle)
                  );

                  // Create arrow lines
                  const arrowGeometry =
                    new THREE.BufferGeometry().setFromPoints([
                      wing1,
                      arrowTip,
                      wing2,
                    ]);
                  const arrowLine = new THREE.Line(
                    arrowGeometry,
                    new THREE.LineBasicMaterial({
                      color: 0x0080ff,
                      linewidth: 2,
                    })
                  );
                  arrowLine.position.copy(mesh.position);
                  markStructureObject(arrowLine.uuid);
                  sceneRef.current.add(arrowLine);

                  // Add starting reference line
                  const startLineGeometry =
                    new THREE.BufferGeometry().setFromPoints([
                      new THREE.Vector3(0, 0, 0),
                      new THREE.Vector3(0, 0, arcRadius),
                    ]);
                  const startLine = new THREE.Line(
                    startLineGeometry,
                    new THREE.LineBasicMaterial({
                      color: 0x0080ff,
                      opacity: 0.5,
                      transparent: true,
                    })
                  );
                  startLine.position.copy(mesh.position);
                  markStructureObject(startLine.uuid);
                  sceneRef.current.add(startLine);

                  // Add rotation angle text
                  const angleCanvas = document.createElement('canvas');
                  const angleContext = angleCanvas.getContext('2d');
                  angleCanvas.width = 128;
                  angleCanvas.height = 64;

                  angleContext.font = 'Bold 24px Arial';
                  angleContext.fillStyle = '#0080ff';
                  angleContext.textAlign = 'center';
                  angleContext.fillText(`${yRotationDeg.toFixed(1)}°`, 64, 40);

                  const angleTexture = new THREE.CanvasTexture(angleCanvas);
                  const angleMaterial = new THREE.SpriteMaterial({
                    map: angleTexture,
                  });
                  const angleSprite = new THREE.Sprite(angleMaterial);
                  angleSprite.scale.set(0.3, 0.15, 1);
                  angleSprite.position.set(
                    mesh.position.x,
                    mesh.position.y + 0.5,
                    mesh.position.z
                  );
                  markStructureObject(angleSprite.uuid);
                  sceneRef.current.add(angleSprite);
                }

                // Show X/Z rotation indicators if they exist
                if (Math.abs(wall.rotation.x) > 0.01) {
                  // Red indicator for X rotation
                  const xRotationDeg = (wall.rotation.x * 180) / Math.PI;
                  const xCanvas = document.createElement('canvas');
                  const xContext = xCanvas.getContext('2d');
                  xCanvas.width = 128;
                  xCanvas.height = 64;

                  xContext.font = 'Bold 20px Arial';
                  xContext.fillStyle = '#ff0000';
                  xContext.textAlign = 'center';
                  xContext.fillText(`X: ${xRotationDeg.toFixed(1)}°`, 64, 40);

                  const xTexture = new THREE.CanvasTexture(xCanvas);
                  const xMaterial = new THREE.SpriteMaterial({ map: xTexture });
                  const xSprite = new THREE.Sprite(xMaterial);
                  xSprite.scale.set(0.25, 0.125, 1);
                  xSprite.position.set(
                    mesh.position.x - 0.3,
                    mesh.position.y + 0.7,
                    mesh.position.z
                  );
                  markStructureObject(xSprite.uuid);
                  sceneRef.current.add(xSprite);
                }

                if (Math.abs(wall.rotation.z) > 0.01) {
                  // Blue indicator for Z rotation
                  const zRotationDeg = (wall.rotation.z * 180) / Math.PI;
                  const zCanvas = document.createElement('canvas');
                  const zContext = zCanvas.getContext('2d');
                  zCanvas.width = 128;
                  zCanvas.height = 64;

                  zContext.font = 'Bold 20px Arial';
                  zContext.fillStyle = '#0000ff';
                  zContext.textAlign = 'center';
                  zContext.fillText(`Z: ${zRotationDeg.toFixed(1)}°`, 64, 40);

                  const zTexture = new THREE.CanvasTexture(zCanvas);
                  const zMaterial = new THREE.SpriteMaterial({ map: zTexture });
                  const zSprite = new THREE.Sprite(zMaterial);
                  zSprite.scale.set(0.25, 0.125, 1);
                  zSprite.position.set(
                    mesh.position.x + 0.3,
                    mesh.position.y + 0.7,
                    mesh.position.z
                  );
                  markStructureObject(zSprite.uuid);
                  sceneRef.current.add(zSprite);
                }
              }
            });
          }

          // Render doors
          if (room.doors && Array.isArray(room.doors)) {
            room.doors.forEach((door, doorIndex) => {
              if (
                !door.dimensions ||
                door.dimensions.x == null ||
                door.dimensions.y == null ||
                door.dimensions.z == null
              ) {
                console.error(
                  `Door ${doorIndex} in room ${roomIndex} missing dimensions:`,
                  door
                );
                return;
              }
              if (
                !door.center ||
                door.center.x == null ||
                door.center.y == null ||
                door.center.z == null
              ) {
                console.error(
                  `Door ${doorIndex} in room ${roomIndex} missing center:`,
                  door
                );
                return;
              }

              const geometry = new THREE.BoxGeometry(
                door.dimensions.x,
                door.dimensions.y,
                door.dimensions.z
              );
              const material = new THREE.MeshStandardMaterial({
                color: 0x8b4513,
              });
              const mesh = new THREE.Mesh(geometry, material);

              mesh.position.set(door.center.x, door.center.y, door.center.z);

              if (door.rotation) {
                mesh.rotation.x = door.rotation.x || 0;
                mesh.rotation.y = door.rotation.y || 0;
                mesh.rotation.z = door.rotation.z || 0;
              }

              markStructureObject(mesh.uuid);
              sceneRef.current.add(mesh);
            });
          }

          // Render windows
          if (room.windows && Array.isArray(room.windows)) {
            room.windows.forEach((window, windowIndex) => {
              if (
                !window.dimensions ||
                window.dimensions.x == null ||
                window.dimensions.y == null ||
                window.dimensions.z == null
              ) {
                console.error(
                  `Window ${windowIndex} in room ${roomIndex} missing dimensions:`,
                  window
                );
                return;
              }
              if (
                !window.center ||
                window.center.x == null ||
                window.center.y == null ||
                window.center.z == null
              ) {
                console.error(
                  `Window ${windowIndex} in room ${roomIndex} missing center:`,
                  window
                );
                return;
              }

              const geometry = new THREE.BoxGeometry(
                window.dimensions.x,
                window.dimensions.y,
                window.dimensions.z
              );
              const material = new THREE.MeshStandardMaterial({
                color: 0x87ceeb,
                opacity: 0.7,
                transparent: true,
              });
              const mesh = new THREE.Mesh(geometry, material);

              mesh.position.set(
                window.center.x,
                window.center.y,
                window.center.z
              );

              if (window.rotation) {
                mesh.rotation.x = window.rotation.x || 0;
                mesh.rotation.y = window.rotation.y || 0;
                mesh.rotation.z = window.rotation.z || 0;
              }

              markStructureObject(mesh.uuid);
              sceneRef.current.add(mesh);
            });
          }

          // Render objects
          if (room.objects && Array.isArray(room.objects)) {
            room.objects.forEach((object, objectIndex) => {
              if (
                !object.dimensions ||
                object.dimensions.x == null ||
                object.dimensions.y == null ||
                object.dimensions.z == null
              ) {
                console.error(
                  `Object ${objectIndex} in room ${roomIndex} missing dimensions:`,
                  object
                );
                return;
              }
              if (
                !object.center ||
                object.center.x == null ||
                object.center.y == null ||
                object.center.z == null
              ) {
                console.error(
                  `Object ${objectIndex} in room ${roomIndex} missing center:`,
                  object
                );
                return;
              }

              // Log object details including category
              console.log(`Object ${objectIndex} in room ${roomIndex}:`, {
                category: object.category,
                center: object.center,
                dimensions: object.dimensions,
                id: object.id,
              });

              const geometry = new THREE.BoxGeometry(
                object.dimensions.x,
                object.dimensions.y,
                object.dimensions.z
              );
              const material = new THREE.MeshStandardMaterial({
                color: 0x4caf50,
              });
              const mesh = new THREE.Mesh(geometry, material);

              mesh.position.set(
                object.center.x,
                object.center.y,
                object.center.z
              );

              if (object.rotation) {
                console.log(
                  `Object ${objectIndex} (${object.category || 'unknown'}) rotation:`,
                  {
                    x: object.rotation.x,
                    y: object.rotation.y,
                    z: object.rotation.z,
                    xDeg: ((object.rotation.x * 180) / Math.PI).toFixed(2),
                    yDeg: ((object.rotation.y * 180) / Math.PI).toFixed(2),
                    zDeg: ((object.rotation.z * 180) / Math.PI).toFixed(2),
                  }
                );
                mesh.rotation.x = object.rotation.x || 0;
                mesh.rotation.y = object.rotation.y || 0;
                mesh.rotation.z = object.rotation.z || 0;
              } else {
                console.log(
                  `Object ${objectIndex} (${object.category || 'unknown'}) has no rotation`
                );
              }

              markStructureObject(mesh.uuid);
              mesh.castShadow = true;
              mesh.receiveShadow = true;
              sceneRef.current.add(mesh);
            });
          }
        });

        // Center camera on structure
        const box = new THREE.Box3().setFromObject(sceneRef.current);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        cameraRef.current.position.set(
          center.x + maxDim,
          center.y + maxDim,
          center.z + maxDim
        );
        cameraRef.current.lookAt(center);
        controlsRef.current.target.copy(center);
        controlsRef.current.update();

        setLoading(false);
      } catch (err) {
        console.error('Error loading structure:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    // Animation loop
    const animate = () => {
      frameId.current = requestAnimationFrame(animate);
      controlsRef.current.update();
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return;

      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(width, height);
    };

    initScene();
    loadStructure();
    animate();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);

      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }

      releaseRenderer(rendererRef.current);

      if (sceneRef.current) {
        sceneRef.current.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
      }
    };
  }, [structureId]);

  return (
    <div className="pre-transformed-viewer-modal">
      <div className="viewer-container">
        <div className="viewer-header">
          <h3>Pre-Transformed Structure Viewer</h3>
          <button onClick={onClose} className="close-button">
            ×
          </button>
        </div>
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading structure data...</p>
          </div>
        )}
        {error && (
          <div className="error-overlay">
            <p>Error: {error}</p>
          </div>
        )}
        <div ref={mountRef} className="viewer-canvas"></div>
        <div className="viewer-info">
          <p>
            {roomId ? `Room ID: ${roomId}` : `Structure ID: ${structureId}`}
          </p>
          <p className="viewer-controls">
            Controls: Left click + drag to rotate, Right click + drag to pan,
            Scroll to zoom
          </p>
          <p className="viewer-controls">Axes: X=Red, Y=Green, Z=Blue</p>
        </div>
      </div>
    </div>
  );
};

export default PreTransformedViewer;
